import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bot, Check, Copy, FileText, Film, Hash, Image as ImageIcon, Link2, Loader2, MessageCircle, Mic, MicOff, Palette, Paperclip, Play, Plug,
  Plus, RefreshCw, Send, Sparkles, Trash2, Users, X, Zap,
} from "lucide-react";

const CATEGORIES = [
  "Campaign creation", "Campaign launch", "Campaign changes", "Creative brief",
  "Create a design", "Video editing", "Website changes", "Weekly review",
  "Integration", "Taking accesses", "Client escalation", "Retention marketing",
  "Client communication",
];

const errorText = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d || "Something went wrong";
};

const initials = (name) => (name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();

const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const absUrl = (u) => {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? "" : "/"}${u}`;
};

const MENTION_RE = /@([A-Za-z][A-Za-z0-9._-]*(?:\s[A-Za-z][A-Za-z0-9._-]*)?)/g;
function renderMessageText(text, team) {
  if (!text) return null;
  const names = new Set((team || []).map((t) => (t.name || "").toLowerCase()));
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(MENTION_RE.source, "g");
  while ((m = re.exec(text)) !== null) {
    const candidate = m[1].toLowerCase();
    // shrink to a match: full then first-word
    let matched = null;
    if (names.has(candidate)) matched = m[1];
    else if (names.has(candidate.split(" ")[0])) matched = m[1].split(" ")[0];
    if (matched) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      parts.push(<mark className="mention-token" key={`m${m.index}`}>@{matched}</mark>);
      last = m.index + 1 + matched.length;
    }
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

function AttachmentTile({ att }) {
  const href = absUrl(att.url);
  if (att.kind === "image") {
    return (
      <a className="att-image" href={href} target="_blank" rel="noreferrer" data-testid={`att-image-${att.name}`}>
        <img src={href} alt={att.name} />
      </a>
    );
  }
  if (att.kind === "video") {
    return (
      <div className="att-video" data-testid={`att-video-${att.name}`}>
        <video controls src={href} preload="metadata" />
      </div>
    );
  }
  return (
    <a className="att-file" href={href} target="_blank" rel="noreferrer" data-testid={`att-file-${att.name}`}>
      <FileText size={16} />
      <div>
        <b>{att.name}</b>
        <small>{att.mime || "file"}</small>
      </div>
    </a>
  );
}


function Page({ title, eyebrow, subtitle, action, children }) {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          {subtitle && <p className="muted">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ---------- AI prompt widget ---------- */
export function AIPromptBox({ api, kind, onFill }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const runParse = async (text) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/ai/parse", { kind, prompt: text });
      onFill(r.data);
      setPrompt("");
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!prompt.trim() || busy) return;
    await runParse(prompt.trim());
  };

  const toggleMic = async () => {
    if (recording) {
      recRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) return;
        setBusy(true); setErr("");
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const r = await api.post("/ai/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = (r.data && r.data.text) || "";
          if (text) {
            setPrompt(text);
            await runParse(text);
          } else {
            setErr("Couldn’t hear anything — try again");
          }
        } catch (e) { setErr(errorText(e)); }
        finally { setBusy(false); }
      };
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setErr("Microphone permission is needed to record");
    }
  };

  return (
    <div className="ai-box" data-testid={`ai-box-${kind}`}>
      <div className="ai-head">
        <Sparkles size={14} />
        <b>Ask AI to fill this for you</b>
        <span>Claude Sonnet 5 · Whisper</span>
      </div>
      <div className="ai-row">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={
            kind === "task"
              ? "e.g. Ask Nisha to design a hero banner for Luma Skin due tomorrow 6pm, high priority"
              : kind === "brief"
                ? "e.g. Static creative for Morrow Home, purpose: drive winter collection clicks, mood: warm neutrals"
                : "e.g. Today I’ll finalise the Luma campaign, review Peak creatives, and send Q3 report"
          }
          data-testid={`ai-prompt-${kind}`}
          rows={2}
          disabled={busy}
        />
        <div className="ai-actions">
          <button
            className={recording ? "ghost-button rec" : "ghost-button"}
            type="button"
            onClick={toggleMic}
            disabled={busy && !recording}
            data-testid={`ai-mic-${kind}`}
            title={recording ? "Stop recording" : "Record voice note"}
          >
            {recording ? <MicOff size={14} /> : <Mic size={14} />}
            {recording ? "Stop" : "Voice"}
          </button>
          <button
            className="primary-button compact"
            type="button"
            onClick={submit}
            disabled={busy || !prompt.trim()}
            data-testid={`ai-submit-${kind}`}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <Bot size={14} />}
            {busy ? "Thinking" : "Fill it in"}
          </button>
        </div>
      </div>
      {err && <div className="error" data-testid={`ai-error-${kind}`}>{err}</div>}
    </div>
  );
}

/* ---------- To-do tab ---------- */
export function Todos({ api, user, team }) {
  const [date, setDate] = useState(todayISO());
  const [mine, setMine] = useState([]);
  const [supervised, setSupervised] = useState([]);
  const [note, setNote] = useState("");
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [myTodo, setMyTodo] = useState(null);

  const reload = async () => {
    try {
      const r = await api.get("/todos");
      setMine(r.data.mine || []);
      setSupervised(r.data.supervised || []);
      const found = (r.data.mine || []).find((t) => t.date === date);
      setMyTodo(found || null);
      setItems(found?.items || []);
      setNote(found?.note || "");
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [date]);

  const persist = async (nextItems, nextNote) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/todos", { date, items: nextItems, note: nextNote });
      setMyTodo(r.data);
      setItems(r.data.items || []);
      setNote(r.data.note || "");
      await reload();
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  const addItem = async () => {
    if (!newItem.trim()) return;
    const next = [...items, { id: "ti_" + Math.random().toString(36).slice(2, 8), content: newItem.trim(), done: false, created_at: new Date().toISOString() }];
    setNewItem("");
    await persist(next, note);
  };

  const toggle = async (id) => {
    const next = items.map((x) => x.id === id ? { ...x, done: !x.done, completed_at: !x.done ? new Date().toISOString() : null } : x);
    await persist(next, note);
  };

  const remove = async (id) => {
    const next = items.filter((x) => x.id !== id);
    await persist(next, note);
  };

  const saveNote = async () => { await persist(items, note); };

  const aiFill = async (data) => {
    const newItems = (data.items || []).map((x, i) => ({ id: "ti_" + Math.random().toString(36).slice(2, 8), content: x.content, done: false, created_at: new Date().toISOString() }));
    await persist([...(items || []), ...newItems], data.note || note);
  };

  return (
    <Page
      title="To-do"
      eyebrow="DAILY LOG"
      subtitle="Share what you’re working on today. Your supervisor sees the plan in real time."
      action={
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="date-picker"
          data-testid="todo-date-input"
        />
      }
    >
      <div className="todo-layout">
        <section className="todo-panel">
          <div className="panel-head">
            <div>
              <p className="eyebrow">MY PLAN · {date}</p>
              <h3>What are you shipping today?</h3>
            </div>
            {busy && <Loader2 size={16} className="spin muted" />}
          </div>

          <AIPromptBox api={api} kind="todo" onFill={aiFill} />

          <div className="todo-list">
            {items.length === 0 && <p className="muted small" data-testid="todo-empty">No items yet. Add your first plan for the day.</p>}
            {items.map((it) => (
              <div className={`todo-item ${it.done ? "done" : ""}`} key={it.id} data-testid={`todo-item-${it.id}`}>
                <button className="check" onClick={() => toggle(it.id)} data-testid={`todo-toggle-${it.id}`}>
                  {it.done && <Check size={12} />}
                </button>
                <span>{it.content}</span>
                <button className="icon-button" onClick={() => remove(it.id)} data-testid={`todo-remove-${it.id}`}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>

          <div className="todo-composer">
            <input
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addItem(); }}
              placeholder="Add a task for today"
              data-testid="todo-input"
            />
            <button className="primary-button compact" onClick={addItem} disabled={!newItem.trim() || busy} data-testid="todo-add-button"><Plus size={14} /> Add</button>
          </div>

          <label className="todo-note">
            <span>Note for your supervisor (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              placeholder="Any context, blockers or priorities…"
              data-testid="todo-note-input"
            />
          </label>
          {err && <div className="error" data-testid="todo-error">{err}</div>}
        </section>

        <aside className="todo-side">
          <div className="panel">
            <div className="panel-head"><div><p className="eyebrow">YOUR HISTORY</p><h3>Past plans</h3></div></div>
            {mine.length === 0 && <p className="muted small">No history yet.</p>}
            {mine.slice(0, 8).map((t) => (
              <button className="history-row" key={t.id} onClick={() => setDate(t.date)} data-testid={`todo-history-${t.id}`}>
                <b>{t.date}</b>
                <small>{t.items?.length || 0} items · {t.items?.filter((x) => x.done).length || 0} done</small>
              </button>
            ))}
          </div>
          {supervised.length > 0 && (
            <div className="panel">
              <div className="panel-head"><div><p className="eyebrow">TEAM YOU SUPERVISE</p><h3>Today’s plans</h3></div></div>
              {supervised.filter((t) => t.date === date).map((t) => (
                <div className="supervised-row" key={t.id} data-testid={`todo-supervised-${t.id}`}>
                  <b>{t.user_name}</b>
                  <small>{t.items?.length || 0} items · {t.items?.filter((x) => x.done).length || 0} done</small>
                  <div className="supervised-items">
                    {(t.items || []).slice(0, 5).map((it) => (
                      <span key={it.id} className={it.done ? "done" : ""}>{it.done ? "✓" : "•"} {it.content}</span>
                    ))}
                  </div>
                  {t.note && <p className="muted small">“{t.note}”</p>}
                </div>
              ))}
              {supervised.filter((t) => t.date === date).length === 0 && <p className="muted small">No plans logged for {date} yet.</p>}
            </div>
          )}
        </aside>
      </div>
    </Page>
  );
}

/* ---------- Integrations tab ---------- */
const PROVIDER_ORDER = ["meta", "google", "ga4", "shopify"];
const PROVIDER_META = {
  meta: { color: "#1877f2", label: "Meta Ads", tag: "M" },
  google: { color: "#0f9d58", label: "Google Ads", tag: "G" },
  ga4: { color: "#f9ab00", label: "Google Analytics 4", tag: "A" },
  shopify: { color: "#96bf48", label: "Shopify", tag: "S" },
};

export function Integrations({ api, projects, user }) {
  const [selected, setSelected] = useState(projects[0]?.id || null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // {provider, form}
  const [metaSnapshot, setMetaSnapshot] = useState(null);
  const [metaBanner, setMetaBanner] = useState("");

  const project = projects.find((p) => p.id === selected);
  const canEdit = user?.role === "Admin" || project?.supervisor === user?.name;

  const load = async () => {
    if (!selected) return;
    setBusy(true); setErr("");
    try { const r = await api.get(`/project-integrations/${selected}`); setData(r.data); }
    catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  const loadMetaSnapshot = async () => {
    if (!selected) return;
    try {
      const r = await api.get(`/integrations/meta/${selected}/data`);
      setMetaSnapshot(r.data);
    } catch (e) { setMetaSnapshot(null); }
  };

  useEffect(() => {
    load();
    setMetaSnapshot(null);
    // Detect ?meta_ok=1 return from OAuth
    const params = new URLSearchParams(window.location.search);
    if (params.get("meta_ok") && params.get("meta_project")) {
      setMetaBanner("✓ Meta connected — pulling today’s data…");
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
      setTimeout(() => setMetaBanner(""), 4000);
    } else if (params.get("meta_error")) {
      setMetaBanner("Meta couldn’t connect: " + params.get("meta_error"));
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
    // eslint-disable-next-line
  }, [selected]);

  useEffect(() => {
    if (data?.providers?.meta?.status === "Connected") loadMetaSnapshot();
    // eslint-disable-next-line
  }, [data]);

  const openEditor = (provider) => setEditing({ provider, form: { account_id: "", access_token: "", refresh_token: "", app_id: "", app_secret: "" } });
  const closeEditor = () => setEditing(null);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.put(`/project-integrations/${selected}/${editing.provider}`, editing.form);
      setData(r.data);
      setEditing(null);
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async (provider) => {
    setBusy(true); setErr("");
    try {
      const r = await api.delete(`/project-integrations/${selected}/${provider}`);
      setData(r.data);
      if (provider === "meta") setMetaSnapshot(null);
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  const connectMeta = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/integrations/meta/${selected}/authorize`);
      window.location.href = r.data.url;
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <Page
      title="Integrations"
      eyebrow="DATA LAYER"
      subtitle="Wire each project into Meta, Google Ads, GA4 and Shopify so tasks pull real campaign data."
    >
      <div className="integrations-layout">
        <aside className="integrations-side">
          <p className="eyebrow">PROJECTS</p>
          {projects.map((p) => (
            <button
              key={p.id}
              className={selected === p.id ? "int-project active" : "int-project"}
              onClick={() => setSelected(p.id)}
              data-testid={`integration-project-${p.id}`}
            >
              <span className="project-logo" style={p.accent_color ? { background: p.accent_color + "22", color: p.accent_color } : undefined}>{p.brand[0]}</span>
              <div><b>{p.brand}</b><small>{p.website}</small></div>
            </button>
          ))}
        </aside>

        <section className="integrations-main">
          {metaBanner && <div className="meta-banner" data-testid="meta-banner">{metaBanner}</div>}
          {err && <div className="error" data-testid="integrations-error">{err}</div>}
          {!data && busy && <div className="muted small"><Loader2 size={14} className="spin" /> Loading…</div>}
          {data && (
            <div className="int-grid">
              {PROVIDER_ORDER.map((pid) => {
                const meta = PROVIDER_META[pid];
                const p = data.providers[pid];
                const connected = p.status === "Connected";
                const isMeta = pid === "meta";
                return (
                  <div className={`int-card ${connected ? "on" : "off"}`} key={pid} data-testid={`integration-card-${pid}`}>
                    <div className="int-card-head">
                      <span className="int-tag" style={{ background: meta.color }}>{meta.tag}</span>
                      <div>
                        <b>{p.name}</b>
                        <small className={connected ? "connected" : "warning-text"}>{p.status}</small>
                      </div>
                    </div>
                    <div className="int-body">
                      {connected ? (
                        <>
                          <div className="int-line"><span>Account ID</span><b>{p.account_id || "—"}</b></div>
                          <div className="int-line"><span>App ID</span><b>{p.app_id_mask || "—"}</b></div>
                          <div className="int-line"><span>Access token</span><b>{p.access_token_mask || "—"}</b></div>
                          <div className="int-line"><span>Since</span><b>{fmtDateTime(p.connected_at)}</b></div>
                        </>
                      ) : (
                        <p className="muted small">Add your OAuth app credentials to fetch campaigns, ad sets and metrics for this project.</p>
                      )}
                      <p className="docs-link"><a href={p.docs} target="_blank" rel="noreferrer">Setup guide ↗</a></p>
                    </div>
                    {canEdit ? (
                      <div className="int-actions">
                        {isMeta && p.app_id_mask && !connected && (
                          <button className="primary-button compact" onClick={connectMeta} disabled={busy} data-testid="integration-meta-connect">
                            <Zap size={14} /> Connect Meta
                          </button>
                        )}
                        {connected && <button className="ghost-button" onClick={() => disconnect(pid)} data-testid={`integration-disconnect-${pid}`}>Disconnect</button>}
                        <button className="ghost-button" onClick={() => openEditor(pid)} data-testid={`integration-edit-${pid}`}>{connected ? "Edit creds" : "Add credentials"}</button>
                      </div>
                    ) : (
                      <p className="muted small">Only the project supervisor or an admin can update credentials.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {data?.providers?.meta?.status === "Connected" && (
            <div className="meta-snap" data-testid="meta-snapshot">
              <div className="panel-head">
                <div>
                  <p className="eyebrow">META · TODAY</p>
                  <h3>Live from Facebook</h3>
                </div>
                <button className="text-button" onClick={loadMetaSnapshot} data-testid="meta-refresh"><RefreshCw size={13} /> Refresh</button>
              </div>
              {!metaSnapshot ? (
                <p className="muted small"><Loader2 size={13} className="spin" /> Fetching Meta data…</p>
              ) : (
                <>
                  <div className="meta-kpis">
                    <div><span>SPEND</span><b>{metaSnapshot.today?.spend || "0"}</b></div>
                    <div><span>IMPRESSIONS</span><b>{metaSnapshot.today?.impressions || "0"}</b></div>
                    <div><span>CLICKS</span><b>{metaSnapshot.today?.clicks || "0"}</b></div>
                    <div><span>CTR</span><b>{metaSnapshot.today?.ctr ? parseFloat(metaSnapshot.today.ctr).toFixed(2) + "%" : "—"}</b></div>
                  </div>
                  <div className="meta-cols">
                    <div>
                      <p className="eyebrow small">AD ACCOUNTS</p>
                      {(metaSnapshot.accounts || []).slice(0, 6).map((a) => (
                        <div className="meta-row" key={a.id}><b>{a.name}</b><small>{a.id} · {a.currency}</small></div>
                      ))}
                    </div>
                    <div>
                      <p className="eyebrow small">CAMPAIGNS</p>
                      {(metaSnapshot.campaigns || []).slice(0, 8).map((c) => (
                        <div className="meta-row" key={c.id}><b>{c.name}</b><small>{c.status} · {c.objective}</small></div>
                      ))}
                      {(!metaSnapshot.campaigns || metaSnapshot.campaigns.length === 0) && <p className="muted small">No campaigns in this account yet.</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={closeEditor}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>{PROVIDER_META[editing.provider].label} credentials</h2>
              <button className="icon-button" onClick={closeEditor} data-testid="integration-close-editor"><X size={18} /></button>
            </div>
            <p className="muted small">Values are stored securely on the server. Paste from your OAuth app.</p>
            {editing.provider === "meta" && (
              <div className="meta-hint">
                <b>Redirect URI</b>
                <code>{window.location.origin}/api/integrations/meta/callback</code>
                <small>Add this URL to your Meta app under Facebook Login → Valid OAuth Redirect URIs.</small>
              </div>
            )}
            {(editing.provider === "meta" ? [["app_id", "App ID"], ["app_secret", "App secret"]] : [["account_id", "Account / Ad account ID"], ["app_id", "App ID / Client ID"], ["app_secret", "Client secret"], ["access_token", "Access token"], ["refresh_token", "Refresh token"]]).map(([k, label]) => (
              <label key={k}>
                {label}
                <input
                  value={editing.form[k] || ""}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, [k]: e.target.value } })}
                  data-testid={`integration-input-${k}`}
                  autoComplete="off"
                  spellCheck={false}
                />
              </label>
            ))}
            <button className="primary-button" onClick={save} disabled={busy} data-testid="integration-save-button">
              {busy ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save credentials
            </button>
          </div>
        </div>
      )}
    </Page>
  );
}

/* ---------- Creative upload widget ---------- */
export function CreativeUploader({ api, projectId, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(0);

  const trigger = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !projectId) return;
    setErr(""); setProgress(0); setBusy(true);
    const isVideo = file.type.startsWith("video/") || /\.(mp4|mov|webm|mkv|m4v)$/i.test(file.name);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", projectId);
    fd.append("kind", isVideo ? "video" : "design");
    fd.append("title", file.name.replace(/\.[^.]+$/, ""));
    try {
      const r = await api.post("/creatives", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => p.total && setProgress(Math.round((p.loaded / p.total) * 100)),
      });
      onUploaded?.(r.data);
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); setProgress(0); }
  };

  return (
    <div className="creative-upload">
      <input
        ref={inputRef}
        type="file"
        style={{ display: "none" }}
        accept="image/*,video/*,application/pdf"
        onChange={onFile}
        data-testid="creative-file-input"
      />
      <button className="primary-button compact" onClick={trigger} disabled={busy} data-testid="creative-upload-button">
        {busy ? <><Loader2 size={14} className="spin" /> {progress ? `${progress}%` : "Uploading"}</> : <><Plus size={14} /> Upload creative</>}
      </button>
      {err && <div className="error" data-testid="creative-upload-error">{err}</div>}
    </div>
  );
}

/* ---------- Creative card renderer ---------- */
function CreativeCard({ api, creative, onDelete }) {
  const isVideo = creative.is_video || (creative.content_type || "").startsWith("video/");
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${creative.storage_path}`;
  return (
    <div className="creative-card" data-testid={`creative-card-${creative.id}`}>
      <div className="creative-media">
        {isVideo ? (
          <video controls src={src} preload="metadata" />
        ) : (creative.content_type || "").startsWith("image/") ? (
          <img src={src} alt={creative.title} loading="lazy" />
        ) : (
          <a href={src} target="_blank" rel="noreferrer" className="file-badge">{creative.content_type?.split("/")[1] || "file"}</a>
        )}
      </div>
      <div className="creative-info">
        <b>{creative.title}</b>
        <small>{creative.uploader_name} · {fmtDateTime(creative.created_at)}</small>
        <div className="creative-actions">
          <a className="text-button" href={src} target="_blank" rel="noreferrer" data-testid={`creative-open-${creative.id}`}>Open ↗</a>
          {onDelete && (
            <button className="text-button danger" onClick={() => onDelete(creative.id)} data-testid={`creative-delete-${creative.id}`}>Delete</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Project detail (briefs + creatives inside a project) ---------- */
export function ProjectDetail({ api, project, user, team, back, briefs, reload }) {
  const [tab, setTab] = useState("brand");
  const [creatives, setCreatives] = useState([]);
  const [briefOpen, setBriefOpen] = useState(null); // 'creative' | 'website' | null
  const [briefForm, setBriefForm] = useState({
    kind: "Static", project: project.brand, assignee: "",
    purpose: "", details: "", references: "",
  });
  const [briefErr, setBriefErr] = useState("");
  const [briefSaving, setBriefSaving] = useState(false);
  const accent = project.accent_color || "#5a1ef5";

  const loadCreatives = async () => {
    try {
      const r = await api.get(`/creatives?project_id=${project.id}`);
      setCreatives(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadCreatives(); /* eslint-disable-next-line */ }, [project.id]);

  const projectBriefs = (briefs || []).filter((b) => b.project === project.brand);
  const creativeBriefs = projectBriefs.filter((b) => ["Static", "Carousel", "Video"].includes(b.kind));
  const websiteBriefs = projectBriefs.filter((b) => !["Static", "Carousel", "Video"].includes(b.kind));

  const openBrief = (variant) => {
    const isWebsite = variant === "website";
    setBriefForm({
      kind: isWebsite ? "Home page" : "Static",
      project: project.brand, assignee: "",
      purpose: "", details: "", references: "",
    });
    setBriefErr("");
    setBriefOpen(variant);
  };

  const saveBrief = async () => {
    setBriefErr(""); setBriefSaving(true);
    try {
      await api.post("/briefs", briefForm);
      setBriefOpen(null);
      await reload();
    } catch (e) { setBriefErr(errorText(e)); }
    finally { setBriefSaving(false); }
  };

  const aiFillBrief = (data) => {
    const types = briefOpen === "website"
      ? ["Home page", "Collection page", "Product page", "Cart page", "Checkout page"]
      : ["Static", "Carousel", "Video"];
    setBriefForm({
      ...briefForm,
      kind: types.includes(data.kind) ? data.kind : briefForm.kind,
      assignee: team.some((t) => t.name === data.assignee) ? data.assignee : briefForm.assignee,
      purpose: data.purpose || briefForm.purpose,
      details: data.details || briefForm.details,
      references: data.references || briefForm.references,
    });
  };

  const removeCreative = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try { await api.delete(`/creatives/${id}`); await loadCreatives(); }
    catch (e) { alert(errorText(e)); }
  };

  const types = briefOpen === "website"
    ? ["Home page", "Collection page", "Product page", "Cart page", "Checkout page"]
    : ["Static", "Carousel", "Video"];

  return (
    <div className="page">
      <div className="project-detail-head" style={{ borderTop: `4px solid ${accent}` }}>
        <button className="ghost-button" onClick={back} data-testid="project-detail-back"><ChevronLeftIcon /> All projects</button>
        <div className="pd-brand">
          <span className="project-logo large" style={{ background: accent + "22", color: accent }}>{project.brand[0]}</span>
          <div>
            <h1>{project.brand}</h1>
            <p className="muted">{project.website} · POC {project.poc} · Supervisor {project.supervisor}</p>
          </div>
        </div>
      </div>

      <div className="pd-tabs">
        {[
          ["brand", "Brand"],
          ["creative-briefs", `Creative briefs (${creativeBriefs.length})`],
          ["website-briefs", `Website briefs (${websiteBriefs.length})`],
          ["creatives", `Creatives folder (${creatives.length})`],
        ].map(([k, l]) => (
          <button
            key={k}
            className={tab === k ? "pd-tab active" : "pd-tab"}
            onClick={() => setTab(k)}
            data-testid={`pd-tab-${k}`}
            style={tab === k ? { color: accent, boxShadow: `inset 0 -2px 0 ${accent}` } : undefined}
          >{l}</button>
        ))}
      </div>

      {tab === "brand" && (
        <div className="pd-brand-grid">
          <div className="panel">
            <p className="eyebrow">SERVICES</p>
            <div className="service-list">{project.services.map((s) => <span key={s} className="pill">{s}</span>)}</div>
            <p className="eyebrow" style={{ marginTop: 20 }}>CONNECTIONS</p>
            <div className="connection-list">
              {Object.entries(project.connections || {}).map(([key, value]) => (
                <div key={key}><span>{key.toUpperCase()}</span><small className={value === "Connected" ? "connected" : "warning-text"}>{value}</small></div>
              ))}
            </div>
          </div>
          <div className="panel">
            <p className="eyebrow">ACCOUNT HEALTH</p>
            <div className="health-block">Current <b style={{ color: accent }}>{project.health}%</b><i style={{ width: `${project.health}%`, background: `linear-gradient(90deg, ${accent}77, ${accent})` }} /></div>
            <p className="muted small" style={{ marginTop: 14 }}>Health blends campaign pacing, task completion and connector uptime.</p>
          </div>
        </div>
      )}

      {(tab === "creative-briefs" || tab === "website-briefs") && (
        <div>
          <div className="pd-section-head">
            <p className="eyebrow">{tab === "website-briefs" ? "WEBSITE BRIEFS" : "CREATIVE BRIEFS"}</p>
            <button className="primary-button compact" onClick={() => openBrief(tab === "website-briefs" ? "website" : "creative")} data-testid={`brief-new-${tab === "website-briefs" ? "website" : "creative"}`}>
              <Plus size={14} /> New {tab === "website-briefs" ? "website" : "creative"} brief
            </button>
          </div>
          <div className="brief-list">
            {(tab === "website-briefs" ? websiteBriefs : creativeBriefs).length === 0 && <p className="muted small">No briefs yet — hit “New brief” to add one.</p>}
            {(tab === "website-briefs" ? websiteBriefs : creativeBriefs).map((b) => (
              <div className="brief-card" key={b.id} data-testid={`brief-card-${b.id}`} style={{ borderLeft: `3px solid ${accent}` }}>
                <div className="brief-card-head">
                  <span className="pill" style={{ background: accent + "22", color: accent }}>{b.kind}</span>
                  <small className="muted">{fmtDateTime(b.created_at)}</small>
                </div>
                <b>{b.purpose || "Untitled brief"}</b>
                <p className="muted small">{b.details}</p>
                <small className="muted">{b.assignee ? `Assigned to ${b.assignee}` : "Unassigned"} · by {b.created_by}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "creatives" && (
        <div>
          <div className="pd-section-head">
            <p className="eyebrow">CREATIVES FOLDER</p>
            <CreativeUploader api={api} projectId={project.id} onUploaded={loadCreatives} />
          </div>
          {creatives.length === 0 ? (
            <p className="muted small">Designers and editors will drop files here — visible to you as POC/supervisor.</p>
          ) : (
            <div className="creative-grid">
              {creatives.map((c) => (
                <CreativeCard
                  key={c.id}
                  api={api}
                  creative={c}
                  onDelete={(c.uploader_email === (user.email || "").toLowerCase() || user.role === "Admin") ? removeCreative : null}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {briefOpen && (
        <div className="modal-backdrop" onClick={() => setBriefOpen(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>New {briefOpen} brief · {project.brand}</h2>
              <button className="icon-button" onClick={() => setBriefOpen(null)} data-testid="brief-close-button"><X size={18} /></button>
            </div>
            <AIPromptBox api={api} kind="brief" onFill={aiFillBrief} />
            <label>{briefOpen === "website" ? "Page type" : "Media type"}
              <div className="option-grid">
                {types.map((x) => (
                  <button type="button" key={x} className={briefForm.kind === x ? "option selected" : "option"} onClick={() => setBriefForm({ ...briefForm, kind: x })} data-testid={`brief-type-${x.toLowerCase().replaceAll(" ", "-")}`}>
                    {x}{briefForm.kind === x && <Check size={14} />}
                  </button>
                ))}
              </div>
            </label>
            <label>Assign to (optional)
              <select value={briefForm.assignee} onChange={(e) => setBriefForm({ ...briefForm, assignee: e.target.value })} data-testid="brief-assignee-select">
                <option value="">— Nobody yet —</option>
                {team.map((x) => <option key={x.id}>{x.name}</option>)}
              </select>
            </label>
            <label>Purpose<textarea value={briefForm.purpose} onChange={(e) => setBriefForm({ ...briefForm, purpose: e.target.value })} data-testid="brief-purpose-input" placeholder="What should this work achieve?" /></label>
            <label>{briefOpen === "website" ? "Website brief" : "Creative brief"}
              <textarea className="big-textarea" value={briefForm.details} onChange={(e) => setBriefForm({ ...briefForm, details: e.target.value })} data-testid="brief-details-input" placeholder="Add the brief, context and deliverables…" />
            </label>
            <label>Reference links<textarea value={briefForm.references} onChange={(e) => setBriefForm({ ...briefForm, references: e.target.value })} data-testid="brief-references-input" placeholder="Paste links, one per line" /></label>
            {briefErr && <div className="error" data-testid="brief-error">{briefErr}</div>}
            <button className="primary-button" onClick={saveBrief} disabled={briefSaving} data-testid="brief-save-button">
              {briefSaving ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save brief
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
);

/* ---------- Global Creatives tab ---------- */
export function CreativesLibrary({ api, projects, user, openProject }) {
  const [creatives, setCreatives] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/creatives"); setCreatives(r.data); }
    catch (e) {}
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const c of creatives) { (g[c.project_id] ||= []).push(c); }
    return g;
  }, [creatives]);

  const removeCreative = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try { await api.delete(`/creatives/${id}`); await load(); }
    catch (e) { alert(errorText(e)); }
  };

  return (
    <Page
      title="Creatives folder"
      eyebrow="ASSET LIBRARY"
      subtitle="Every design and video the team ships, grouped by project."
    >
      {busy && creatives.length === 0 && <div className="muted small"><Loader2 size={14} className="spin" /> Loading…</div>}
      {!busy && creatives.length === 0 && <p className="muted small" data-testid="creatives-empty">Nothing here yet — designers and editors upload from a project’s Creatives folder.</p>}
      {projects.map((p) => {
        const list = grouped[p.id] || [];
        if (!list.length) return null;
        const accent = p.accent_color || "#5a1ef5";
        return (
          <div className="creative-group" key={p.id} data-testid={`creative-group-${p.id}`}>
            <div className="cg-head">
              <span className="project-logo" style={{ background: accent + "22", color: accent }}>{p.brand[0]}</span>
              <div><b>{p.brand}</b><small>{list.length} file{list.length !== 1 ? "s" : ""}</small></div>
              <button className="text-button" onClick={() => openProject(p.id)} data-testid={`creative-open-project-${p.id}`}>Open project →</button>
            </div>
            <div className="creative-grid">
              {list.map((c) => (
                <CreativeCard
                  key={c.id}
                  api={api}
                  creative={c}
                  onDelete={(c.uploader_email === (user.email || "").toLowerCase() || user.role === "Admin") ? removeCreative : null}
                />
              ))}
            </div>
          </div>
        );
      })}
    </Page>
  );
}

export function Communication({ api, user, team }) {
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", members: [] });
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [voiceErr, setVoiceErr] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState([]); // [{url,name,kind,mime,size}]
  const [attachErr, setAttachErr] = useState("");
  const [mentionState, setMentionState] = useState({ open: false, query: "", start: 0 });
  const [membersOpen, setMembersOpen] = useState(false);
  const [pendingMembers, setPendingMembers] = useState([]);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recStartRef = useRef(0);
  const scrollRef = useRef(null);
  const lastCursor = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);

  const loadChannels = async () => {
    try {
      const r = await api.get("/channels");
      setChannels(r.data);
      if (!selected && r.data.length) setSelected(r.data[0].id);
    } catch (e) {}
  };

  const loadMessages = async (initial = false) => {
    if (!selected) return;
    const params = !initial && lastCursor.current ? `?after=${encodeURIComponent(lastCursor.current)}` : "";
    try {
      const r = await api.get(`/channels/${selected}/messages${params}`);
      const incoming = r.data || [];
      if (initial) {
        setMessages(incoming);
      } else if (incoming.length) {
        setMessages((prev) => {
          const known = new Set(prev.map((m) => m.id));
          const fresh = incoming.filter((m) => !known.has(m.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
      if (incoming.length) lastCursor.current = incoming[incoming.length - 1].created_at;
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadChannels(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (!selected) return;
    lastCursor.current = null;
    setMessages([]);
    loadMessages(true);

    // Realtime: open a WebSocket for this channel; fall back to polling if it fails.
    let ws = null;
    let pollTimer = null;
    let closed = false;
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + `/api/ws/channels/${selected}`;

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => loadMessages(false), 4000);
    };

    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "message" && payload.message) {
            const m = payload.message;
            setMessages((prev) => {
              if (prev.some((x) => x.id === m.id)) return prev;
              return [...prev, m];
            });
            lastCursor.current = m.created_at;
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        if (!closed) startPolling(); // graceful fallback
      };
      ws.onerror = () => { startPolling(); };
    } catch (_) {
      startPolling();
    }

    return () => {
      closed = true;
      if (ws && ws.readyState <= 1) { try { ws.close(); } catch (_) {} }
      if (pollTimer) clearInterval(pollTimer);
    };
    // eslint-disable-next-line
  }, [selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if ((!text.trim() && pendingAttachments.length === 0) || !selected) return;
    setBusy(true);
    try {
      await api.post(`/channels/${selected}/messages`, { text: text.trim(), attachments: pendingAttachments });
      setText("");
      setPendingAttachments([]);
      await loadMessages(false);
    } catch (e) {}
    finally { setBusy(false); }
  };

  const attachFile = async (fileList) => {
    if (!fileList || fileList.length === 0 || !selected) return;
    setAttachErr("");
    const f = fileList[0];
    if (f.size > 40 * 1024 * 1024) { setAttachErr("File too large — max 40 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const r = await api.post(`/chat-attachments?channel_id=${selected}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingAttachments((prev) => [...prev, r.data]);
    } catch (e) {
      setAttachErr(errorText(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePending = (i) => setPendingAttachments((p) => p.filter((_, idx) => idx !== i));

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const cursor = e.target.selectionStart || val.length;
    // find the token being typed after an '@'
    const upto = val.slice(0, cursor);
    const at = upto.lastIndexOf("@");
    if (at >= 0) {
      const token = upto.slice(at + 1);
      // stop matching once we hit two spaces (not a mention)
      if (!/\s{2,}/.test(token) && token.length <= 40) {
        setMentionState({ open: true, query: token, start: at });
        return;
      }
    }
    if (mentionState.open) setMentionState({ open: false, query: "", start: 0 });
  };

  const applyMention = (member) => {
    const before = text.slice(0, mentionState.start);
    const after = text.slice((inputRef.current && inputRef.current.selectionStart) || text.length);
    const next = `${before}@${member.name} ${after}`;
    setText(next);
    setMentionState({ open: false, query: "", start: 0 });
    setTimeout(() => {
      if (inputRef.current) {
        const pos = (before + `@${member.name} `).length;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const mentionSuggestions = useMemo(() => {
    if (!mentionState.open) return [];
    const q = (mentionState.query || "").toLowerCase();
    return team.filter((t) => (t.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [mentionState, team]);

  const openMembers = () => {
    const ch = channels.find((c) => c.id === selected);
    setPendingMembers(ch?.members || []);
    setMembersOpen(true);
  };

  const saveMembers = async () => {
    try {
      await api.patch(`/channels/${selected}/members`, { members: pendingMembers });
      setMembersOpen(false);
      await loadChannels();
    } catch (e) { alert(errorText(e)); }
  };

  const createChannel = async () => {
    if (!newGroup.name.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/channels", { name: newGroup.name.trim(), kind: "group", members: newGroup.members });
      setCreatingOpen(false);
      setNewGroup({ name: "", members: [] });
      await loadChannels();
      setSelected(r.data.id);
    } catch (e) {}
    finally { setBusy(false); }
  };

  const stopRecording = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
  };

  const startRecording = async () => {
    setVoiceErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach((t) => t.stop());
        if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
        const duration_ms = Date.now() - recStartRef.current;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size || !selected) return;
        setUploading(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          await api.post(`/voice-notes?channel_id=${selected}&duration_ms=${duration_ms}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          await loadMessages(false);
        } catch (e) {
          setVoiceErr(errorText(e));
        } finally { setUploading(false); }
      };
      recRef.current = rec;
      recStartRef.current = Date.now();
      setRecSecs(0);
      rec.start();
      setRecording(true);
      recTimerRef.current = setInterval(() => {
        const s = Math.floor((Date.now() - recStartRef.current) / 1000);
        setRecSecs(s);
        if (s >= 30) stopRecording();
      }, 250);
    } catch (e) {
      setVoiceErr("Microphone permission is needed to record");
    }
  };

  const audioSrc = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

  const current = channels.find((c) => c.id === selected);

  return (
    <Page
      title="Communication"
      eyebrow="TEAM CHAT"
      subtitle="Talk to your team. Create groups for projects, briefs or clients."
      action={<button className="primary-button compact" onClick={() => setCreatingOpen(true)} data-testid="chat-create-group-button"><Plus size={14} /> New group</button>}
    >
      <div className="chat-layout">
        <aside className="chat-side">
          <p className="eyebrow">CHANNELS</p>
          {channels.map((c) => (
            <button
              key={c.id}
              className={selected === c.id ? "chat-channel active" : "chat-channel"}
              onClick={() => setSelected(c.id)}
              data-testid={`chat-channel-${c.id}`}
            >
              <span className="chat-hash"><Hash size={13} /></span>
              <div>
                <b>{c.name}</b>
                <small>{c.last_message ? `${c.last_message.sender_name}: ${(c.last_message.text || c.last_message.transcript || "voice note").slice(0, 40)}` : "No messages yet"}</small>
              </div>
            </button>
          ))}
        </aside>

        <section className="chat-main">
          {current ? (
            <>
              <header className="chat-head">
                <div>
                  <h3>#{current.name}</h3>
                  <small className="muted">{current.kind === "public" ? "Everyone in the workspace" : `${current.members.length} member${current.members.length !== 1 ? "s" : ""}`}</small>
                </div>
                {current.kind !== "public" && (
                  <button className="ghost-button small" onClick={openMembers} data-testid="chat-manage-members"><Users size={13} /> Members</button>
                )}
              </header>
              <div className="chat-messages" ref={scrollRef} data-testid="chat-messages">
                {messages.length === 0 && <p className="muted small">Say hello 👋</p>}
                {messages.map((m) => {
                  const mine = m.sender_email === (user.email || "").toLowerCase();
                  return (
                    <div className={mine ? "chat-msg mine" : "chat-msg"} key={m.id} data-testid={`chat-msg-${m.id}`}>
                      <span className="chat-avatar">{m.sender_avatar || initials(m.sender_name)}</span>
                      <div className="chat-bubble">
                        <div className="chat-meta"><b>{m.sender_name}</b><small>{fmtDateTime(m.created_at)}</small></div>
                        {m.audio_path ? (
                          <div className="voice-msg" data-testid={`voice-msg-${m.id}`}>
                            <audio controls src={audioSrc(m.audio_path)} data-testid={`voice-audio-${m.id}`} />
                            {m.transcript ? <p className="voice-transcript">“{m.transcript}”</p> : <p className="voice-transcript muted">Transcribing…</p>}
                          </div>
                        ) : (
                          <>
                            {m.text && <p className="chat-body">{renderMessageText(m.text, team)}</p>}
                            {(m.attachments || []).length > 0 && (
                              <div className="msg-attachments" data-testid={`msg-attachments-${m.id}`}>
                                {(m.attachments || []).map((a, i) => <AttachmentTile key={i} att={a} />)}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {pendingAttachments.length > 0 && (
                <div className="attach-tray" data-testid="attach-tray">
                  {pendingAttachments.map((a, i) => (
                    <div className="attach-chip" key={i} data-testid={`attach-chip-${i}`}>
                      <span className="attach-kind">{a.kind}</span>
                      <span className="attach-name">{a.name}</span>
                      <button type="button" onClick={() => removePending(i)} data-testid={`attach-remove-${i}`}><X size={12} /></button>
                    </div>
                  ))}
                </div>
              )}
              {attachErr && <div className="error" data-testid="attach-error">{attachErr}</div>}

              <div className="chat-composer">
                <input type="file" ref={fileRef} onChange={(e) => attachFile(e.target.files)} style={{ display: "none" }} accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt" data-testid="chat-file-input" />
                <button type="button" className="ghost-button small" onClick={() => fileRef.current?.click()} disabled={uploading || recording} title="Attach file, photo or video" data-testid="chat-attach-button">
                  <Paperclip size={14} />
                </button>
                <div className="chat-input-wrap">
                  <input
                    ref={inputRef}
                    value={text}
                    onChange={handleTextChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey && !mentionState.open) { e.preventDefault(); send(); }
                      if (e.key === "Escape" && mentionState.open) setMentionState({ open: false, query: "", start: 0 });
                    }}
                    placeholder={recording ? `Recording… ${recSecs}s / 30s` : `Message #${current.name} — type @ to mention`}
                    disabled={recording || uploading}
                    data-testid="chat-input"
                  />
                  {mentionState.open && mentionSuggestions.length > 0 && (
                    <div className="mention-popover" data-testid="mention-popover">
                      {mentionSuggestions.map((sug) => (
                        <button key={sug.id} type="button" className="mention-item" onClick={() => applyMention(sug)} data-testid={`mention-suggest-${sug.id}`}>
                          <span className="chat-avatar sm">{initials(sug.name)}</span>
                          <div><b>{sug.name}</b><small>{sug.role}</small></div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={recording ? "ghost-button rec small" : "ghost-button small"}
                  onClick={recording ? stopRecording : startRecording}
                  disabled={uploading}
                  title={recording ? "Stop and send" : "Record voice note (30s max)"}
                  data-testid="chat-mic-button"
                >
                  {uploading ? <Loader2 size={14} className="spin" /> : recording ? <MicOff size={14} /> : <Mic size={14} />}
                  {recording ? `${recSecs}s` : ""}
                </button>
                <button className="primary-button compact" onClick={send} disabled={busy || (!text.trim() && pendingAttachments.length === 0) || recording || uploading} data-testid="chat-send-button"><Send size={14} /></button>
              </div>
              {voiceErr && <div className="error" data-testid="chat-voice-error">{voiceErr}</div>}
            </>
          ) : (
            <div className="chat-empty muted small">Pick a channel to start chatting.</div>
          )}
        </section>
      </div>

      {membersOpen && current && (
        <div className="modal-backdrop" onClick={() => setMembersOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>#{current.name} members</h2>
              <button className="icon-button" onClick={() => setMembersOpen(false)} data-testid="chat-members-close"><X size={18} /></button>
            </div>
            <p className="muted small">Add or remove teammates. The creator is always a member.</p>
            <div className="check-grid" data-testid="chat-members-grid">
              {team.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  className={pendingMembers.includes((t.email || "").toLowerCase()) ? "check-chip selected" : "check-chip"}
                  onClick={() => {
                    const em = (t.email || "").toLowerCase();
                    setPendingMembers((prev) => prev.includes(em) ? prev.filter((x) => x !== em) : [...prev, em]);
                  }}
                  data-testid={`chat-member-toggle-${t.id}`}
                >
                  {t.name}
                </button>
              ))}
            </div>
            <button className="primary-button" onClick={saveMembers} data-testid="chat-members-save">Save members</button>
          </div>
        </div>
      )}

      {creatingOpen && (
        <div className="modal-backdrop" onClick={() => setCreatingOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>New group</h2>
              <button className="icon-button" onClick={() => setCreatingOpen(false)} data-testid="chat-close-create"><X size={18} /></button>
            </div>
            <label>Group name<input value={newGroup.name} onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })} data-testid="chat-group-name" placeholder="e.g. luma-launch" /></label>
            <label>Add teammates
              <div className="check-grid">
                {team.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className={newGroup.members.includes(t.email) ? "check-chip selected" : "check-chip"}
                    onClick={() => setNewGroup({ ...newGroup, members: newGroup.members.includes(t.email) ? newGroup.members.filter((x) => x !== t.email) : [...newGroup.members, t.email] })}
                    data-testid={`chat-group-member-${t.id}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </label>
            <button className="primary-button" onClick={createChannel} disabled={busy || !newGroup.name.trim()} data-testid="chat-group-save">Create group</button>
          </div>
        </div>
      )}
    </Page>
  );
}
